import { useBetterQueryParams } from '@app/src/hooks/queryParams'
import type { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import qs from 'qs'
import { useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { createUniqueFilters, useGetQueryFilters } from './queryFilters'

export interface StringifyFilterValue {
  name: string
  [key: string]: QueryParamType
  operator: Operators
  uniqueId?: string
}
export type QueryParamType = undefined | string | number | boolean | { [key: string]: unknown } | Array<QueryParamType>
export type StringifyQueryParamsType = {
  url?: string
  queryParams: {
    [key: string]: Array<StringifyFilterValue>
  }
  includeCurrentFilters?: boolean
}

/**
 * Standalone function to stringify query filters into a URL
 * Use this when you don't need to include current filters from the URL
 */
export const stringifyQueryFilters = ({
  url,
  queryParams,
}: Omit<StringifyQueryParamsType, 'includeCurrentFilters'>): string => {
  const filters = queryParams['filters'] ?? []
  const queryString = qs.stringify({ filters }, { encodeValuesOnly: true })
  return `${url ?? origin}?${queryString}`
}

export function useQueryState<T extends QueryParamType = QueryParamType>(
  queryParamName: string,
  defaultValue?: QueryParamType,
): [T, (value: QueryParamType, replace?: boolean) => void] {
  const history = useHistory()
  const params = useBetterQueryParams()

  const setQuery = useCallback(
    (value: QueryParamType, replace?: boolean) => {
      const queryString = qs.stringify({ ...params, [queryParamName]: value }, { encodeValuesOnly: true })

      if (replace) {
        history.replace(`${window.location.pathname}?${queryString}`)
      } else {
        history.push(`${window.location.pathname}?${queryString}`)
      }
    },
    [history, params, queryParamName],
  )

  return [(params[queryParamName] ?? defaultValue) as T, setQuery]
}

/**
 * Hook version of stringifyQueryFilters
 * Use this when you need to include current filters from the URL
 */
export const useStringifyQueryFilters = (): { stringifyQueryFilters: (params: StringifyQueryParamsType) => string } => {
  const { getQueryFilters } = useGetQueryFilters()

  const stringifyQueryFiltersWithCurrent = ({ url, queryParams, includeCurrentFilters }: StringifyQueryParamsType) => {
    if (!includeCurrentFilters) {
      return stringifyQueryFilters({ url, queryParams })
    }

    const currentFilters = getQueryFilters()
    const newFilters = queryParams['filters'] ?? []

    const filters = newFilters.reduce((prev, curr) => {
      return createUniqueFilters(prev, curr.name, curr.value as string | string[], curr.operator, curr.uniqueId)
    }, currentFilters)

    const queryString = qs.stringify({ filters }, { encodeValuesOnly: true })
    return `${url ?? origin}?${queryString}`
  }

  return {
    stringifyQueryFilters: stringifyQueryFiltersWithCurrent,
  }
}
